import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import type {FeatureConfigKey} from '@/models';

interface UseRouteProtectionReturn {
  isAdmin: boolean;
  isEmployee: boolean;
  isStaff: boolean;
  loading: boolean;
  handleLogout: (onLogout: () => void) => void;
  hasAccess: boolean;
  isFeatureLocked: boolean;
}

export function useRouteProtection(
  requireAdmin = false,
  requiredFeature?: FeatureConfigKey,
  requireEmployee = false
): UseRouteProtectionReturn {
  const navigate = useNavigate();
  const { isAdmin, isEmployee, isStaff, loading, user } = useUser();

  const handleLogout = useCallback((onLogout: () => void) => {
    navigate('/');
    onLogout();
  }, [navigate]);

  // Check admin access - requireAdmin means only full admins
  // requireEmployee means both admins and employees can access
  const hasAdminAccess = requireAdmin ? isAdmin : (requireEmployee ? isStaff : true);

  // Check feature access
  let hasFeatureAccess = true;
  let isFeatureLocked = false;

  if (requiredFeature !== undefined && user !== null && !isAdmin) {
    const featureValue = user.featureConfig[requiredFeature];

    if (typeof featureValue === 'boolean') {
      hasFeatureAccess = featureValue;
      isFeatureLocked = !featureValue;
    }
  }

  const hasAccess = hasAdminAccess && hasFeatureAccess;

  // Only show loading if we don't have user data yet AND we need to check permissions
  const isLoading = loading && (requireAdmin || requireEmployee || requiredFeature !== undefined) && user === null;

  return {
    isAdmin,
    isEmployee,
    isStaff,
    loading: isLoading,
    handleLogout,
    hasAccess,
    isFeatureLocked
  };
}